
import { MapPin, User, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Contact {
  name: string;
  phone: string;
  email?: string;
}

interface Address {
  street: string;
  street2?: string;
  city: string;
  province: string;
  postal_code: string;
  country: string;
}

interface AddressDisplayProps {
  title: string;
  address: Address | null;
  contacts: Contact[];
}

const AddressDisplay = ({ title, address, contacts }: AddressDisplayProps) => {
  const handlePhoneClick = (phoneNumber: string) => {
    window.open(`tel:${phoneNumber}`, '_self');
  };

  const handleEmailClick = (email: string) => {
    window.open(`mailto:${email}`, '_self');
  };

  const primaryContact = contacts?.[0]; // First contact is primary

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-primary" />
        <h3 className="font-medium">{title}</h3>
        {contacts && contacts.length > 1 && (
          <span className="text-xs text-muted-foreground">({contacts.length} contacts)</span>
        )}
      </div>
      
      {address && (
        <div className="text-sm text-muted-foreground">
          <div>{address.street}</div>
          {address.street2 && <div>{address.street2}</div>}
          <div>{address.city}, {address.province} {address.postal_code}</div>
          <div>{address.country}</div>
        </div>
      )}

      {primaryContact && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <User className="h-3 w-3 text-muted-foreground" />
            <span className="font-medium">{primaryContact.name}</span>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handlePhoneClick(primaryContact.phone)}
              className="h-7 px-2 text-xs"
            >
              <Phone className="h-3 w-3 mr-1" />
              {primaryContact.phone}
            </Button>
            
            {primaryContact.email && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleEmailClick(primaryContact.email!)}
                className="h-7 px-2 text-xs"
              >
                <Mail className="h-3 w-3 mr-1" />
                {primaryContact.email}
              </Button>
            )}
          </div>

          {contacts && contacts.length > 1 && (
            <div className="text-xs text-muted-foreground">
              +{contacts.length - 1} more contact{contacts.length > 2 ? 's' : ''}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressDisplay;
